import Tooltip from '@components/shared/Tooltip'
import Image from 'next/image'
import { useEffect, useRef } from 'react'
import { Trans, useTranslation } from 'next-i18next'
import { ROUTER_INFO_LIST, ROUTER_INFO } from './useQuoteRoutes'
import { JupiterV6RouteInfo } from 'types/jupiter'
import { milliseconds } from 'date-fns'

function easeOutElastic(x: number): number {
  const c4 = (2 * Math.PI) / 3
  const stiffness = 10
  const damping = 20

  return x === 0
    ? 0
    : x === 1
      ? 1
      : Math.pow(2, -damping * x) * Math.sin((x * stiffness - 0.75) * c4) + 1
}

const RouterDisplay = ({
  fetchingRoute,
  selectedRouter,
}: {
  fetchingRoute: boolean
  selectedRouter: JupiterV6RouteInfo['origin']
}) => {
  const { t } = useTranslation('swap')
  const containerRef = useRef<HTMLDivElement>(null)
  const phaseRef = useRef(0)

  useEffect(() => {
    const items = containerRef.current?.children
    if (!items) return

    const radius = 30 // how far icons move left/right
    const tilt = 4 // how far the camera tilts up/down
    const transitionDurationToSelectedRouter = milliseconds({ seconds: 1 })
    const twoPiFractionPerRouter = 2 / ROUTER_INFO_LIST.length

    let selectedRouterParams: {
      routerIdx: number
      startTime: number
      phaseDifference: number
    } | null = null
    if (selectedRouter != null) {
      const routerIdx = Object.keys(ROUTER_INFO).findIndex(
        (router) => router === selectedRouter,
      )
      const startPhase = phaseRef.current
      const targetPhase = (routerIdx * -twoPiFractionPerRouter + 0.5) * Math.PI

      selectedRouterParams = {
        routerIdx,
        startTime: Date.now(),
        phaseDifference:
          startPhase +
          (targetPhase -
            startPhase +
            // make sure that we always animate in a single direction
            (targetPhase < startPhase ? Math.PI * 2 : 0)),
      }
    }

    let animationFrameId: ReturnType<typeof requestAnimationFrame> | null = null
    const animate = () => {
      for (let i = 0; i < items.length; i++) {
        const el = items[i] as HTMLElement
        const angle = phaseRef.current + i * (Math.PI * twoPiFractionPerRouter)
        const x = radius * Math.cos(angle)
        const z = radius * Math.sin(angle)
        const y = z * (tilt / radius)
        const zNorm = (z + radius) / (2 * radius)
        const scale =
          (1 + 0.5 * zNorm) *
          (selectedRouterParams != null && selectedRouterParams.routerIdx === i
            ? 1.5
            : 1)
        const zIndex = Math.round(10 * zNorm)

        el.style.transform = `translate(${x}px, ${y}px) scale(${scale})`
        el.style.zIndex = `${zIndex}`
      }

      if (fetchingRoute) {
        phaseRef.current += 0.05
      } else if (selectedRouterParams != null) {
        const { startTime, phaseDifference } = selectedRouterParams
        const elapsed = Date.now() - startTime
        const progress = Math.min(
          elapsed / transitionDurationToSelectedRouter,
          1,
        )
        const easedProgress = easeOutElastic(progress)
        phaseRef.current = phaseDifference * easedProgress
      } else {
        phaseRef.current += 0.005
      }

      phaseRef.current = phaseRef.current % (Math.PI * 2)
      animationFrameId = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      if (animationFrameId != null) {
        cancelAnimationFrame(animationFrameId)
      }
    }
  }, [fetchingRoute, selectedRouter])

  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs">
          <Trans
            i18nKey="routers"
            t={t}
            values={{
              routersCount: ROUTER_INFO_LIST.length,
            }}
            components={{
              tooltip: (
                <Tooltip
                  content={
                    <div className="space-y-2">
                      <p>
                        {t('routers-tooltip', {
                          routersCount: ROUTER_INFO_LIST.length,
                        })}
                      </p>
                      {ROUTER_INFO_LIST.map(({ name, src }) => (
                        <div
                          key={`${name}-tooltip-logo`}
                          className="flex items-center gap-1.5"
                        >
                          <Image
                            src={src}
                            alt={`${name} logo`}
                            width={16}
                            height={16}
                          />
                          <p className="text-th-fgd-1">{name}</p>
                        </div>
                      ))}
                    </div>
                  }
                  wrapperClassName="text-th-fgd-2 tooltip-underline"
                />
              ),
            }}
          />
        </p>
      </div>
      <div
        ref={containerRef}
        className="relative flex h-8 w-24 items-center overflow-hidden"
      >
        {ROUTER_INFO_LIST.map(({ name, src }, idx) => (
          <div
            key={`${name}-loader-logo`}
            className="absolute left-1/2 -translate-x-1/2"
            style={{ width: 16, height: 16 }}
          >
            <Image
              src={src}
              alt={`${name} logo`}
              width={16}
              height={16}
              data-idx={idx}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

export default RouterDisplay
